// Ügyfél létrehozása (full-page). A form-INSTANCE itt él (a 422 → mező-hiba a mutációból írható).
// Idempotency-Key a create-en (form-instance-enként stabil, a dupla-POST ellen). Siker → a detail-oldalra.
import { ClientDetailSchema } from '@atlas/contracts';
import {
  BuildingOffice2Icon,
  CreditCardIcon,
  IdentificationIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { FormPage } from '@/components/shared/form-page';
import { LockedSection } from '@/components/shared/locked-section';
import { clientKeys } from '@/features/clients/api';
import { ClientForm } from '@/features/clients/components/client-form';
import {
  EMPTY_CLIENT_FORM,
  buildClientErrorMap,
  clientFormSchema,
  type ClientFormValues,
} from '@/features/clients/form';
import { api } from '@/lib/api';
import { requirePermission } from '@/lib/require-permission';
import { newIdempotencyKey, useEntityMutation } from '@/lib/use-entity-mutation';

/**
 * A kapcsolódó szekciók, amik CSAK a mentés után kezelhetők (a régi CRM tabos szerkesztőjének megfelelői).
 * SORRENDBEN, ahogy az ügyfél-detailen is jönnek majd. A `documents` SZÁNDÉKOSAN nincs itt: a
 * dokumentumkezelés modul ki van vezetve (ROADMAP 5. mérföldkő ⛔).
 * Az ikonok a nav-ból valók, hogy a szekció ránézésre azonosítható legyen ugyanazzal a jellel.
 */
const PENDING_SECTIONS = [
  {
    icon: BuildingOffice2Icon,
    titleKey: 'clients.pendingAccounts',
    descKey: 'clients.pendingAccountsDesc',
  },
  {
    icon: Square3Stack3DIcon,
    titleKey: 'clients.pendingServices',
    descKey: 'clients.pendingServicesDesc',
  },
  {
    icon: CreditCardIcon,
    titleKey: 'clients.pendingPaymentCards',
    descKey: 'clients.pendingPaymentCardsDesc',
  },
  {
    icon: IdentificationIcon,
    titleKey: 'clients.pendingBillingProfiles',
    descKey: 'clients.pendingBillingProfilesDesc',
  },
] as const;

export const Route = createFileRoute('/clients/new')({
  // ROUTE-SZINTŰ jog-guard: `clients.manage` nélkül az oldal URL-lel SEM érhető el — a felhasználó vissza a
  // listára, MIELŐTT bármi renderelne. (A belépő-gomb a listán amúgy is tiltott; a tényleges fal a BFF
  // `@RequirePermissions` + az Odin scope/RLS. A három réteg: `lib/require-permission.ts`.)
  beforeLoad: () => requirePermission('clients.manage', { fallback: '/clients' }),
  component: ClientCreatePage,
});

function ClientCreatePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Form-instance-enként stabil idempotency-kulcs (retry-nál is ugyanaz → nincs dupla ügyfél).
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const form = useForm<ClientFormValues>({
    // A resolver a contract create-sémáját futtatja (a body-t a szerver újra-validálja); a cast csak a
    // nullable/optional output → string-alapú form-értékek eltérést hidalja át (a mezők kezelik az ürességet).
    resolver: zodResolver(clientFormSchema, {
      errorMap: buildClientErrorMap(t),
    }) as Resolver<ClientFormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_CLIENT_FORM,
  });

  const createMutation = useEntityMutation<ClientFormValues, unknown>({
    mutationFn: (values) =>
      api.clients.create.mutation({
        body: values,
        headers: { 'idempotency-key': idempotencyKey },
      }),
    invalidateKeys: [clientKeys.lists()],
    onFieldErrors: (field, messages) =>
      form.setError(field as keyof ClientFormValues, { message: messages.join(', ') }),
    messages: {
      success: t('toast:clients.created'),
      conflict: t('toast:clients.conflict'),
      forbidden: t('toast:clients.forbidden'),
      validation: t('toast:clients.validation'),
      error: t('toast:clients.error'),
    },
  });

  function onSubmit(values: ClientFormValues): void {
    createMutation.mutate(values, {
      onSuccess: (data) => {
        const parsed = ClientDetailSchema.safeParse(data.body);
        if (parsed.success) {
          void navigate({ to: '/clients/$id', params: { id: parsed.data.id } });
        } else {
          void navigate({ to: '/clients' });
        }
      },
    });
  }

  // NINCS oldalon belüli „nincs jogosultság" doboz: a `beforeLoad` guard már a navigáció ELŐTT visszatereli a
  // jogosulatlan felhasználót a listára, tehát ide jog nélkül el sem lehet jutni. Egy renderelés utáni
  // zsákutca-doboz csak azt érné el, hogy a user végigolvassa az oldalt, mire kiderül, hogy nem használhatja.

  return (
    // A cím a TOPBAR morzsájában él (a profil-oldal mintája) — az oldal törzsében nincs h1, mert duplikálná.
    <AppShell
      parent={{ label: t('nav.clients'), to: '/clients' }}
      title={t('clients.create')}
      // ALCÍM NINCS (lásd `clients.$id.tsx`): a morzsa + a cím elég; az alcím a lista-oldalaké.
    >
      <FormPage>
        {/* EGY görgethető hasáb: Alapadatok (a végén a „Létrehozás" gombbal — az CSAK ezt a szekciót küldi
            be), alatta a kapcsolódó szekciók ZÁROLVA (a régi CRM tabjai: Fiókok · Szolgáltatások ·
            Bankkártyák · Számlázási profilok — a Dokumentumok tudatosan kimarad). A szekciók a formON BELÜL
            vannak, de a gomb ALATT: így a create-oldal UGYANOTT hozza a gombot, mint a szerkesztés. */}
        <ClientForm
          form={form}
          mode="create"
          pending={createMutation.isPending}
          onSubmit={onSubmit}
          t={t}
          // Az automatikus számlázás LÉTREHOZÁSKOR fogalmilag nem kapcsolható be: számla elsődleges
          // számlázási profilról áll ki, profilt viszont csak MÁR LÉTEZŐ ügyfélhez lehet kötni (lásd a
          // lenti zárolt „Számlázási profilok" szekciót). A mező tehát nem tiltás, hanem sorrend kérdése.
          autoBillingBlockedReason="create"
        >
          {PENDING_SECTIONS.map((s) => (
            <LockedSection
              key={s.titleKey}
              icon={s.icon}
              title={t(s.titleKey)}
              description={t(s.descKey)}
              hint={t('clients.pendingHint')}
            />
          ))}
        </ClientForm>
      </FormPage>
    </AppShell>
  );
}
